import { useEffect, useState } from 'react';
import { Btn } from '../components/ui/Btn';
import { HojaInferior } from '../components/ui/HojaInferior';
import { notificationsApi } from '../features/branding/api';
import { leerTemaLocal } from '../features/branding/tema';
import type { SesionMe } from '../features/session/api';
import { irA, useRuta } from '../lib/ruta';
import { textos } from '../textos';
import { destinosMas, NAV_MOVIL_FIJO, rutaActiva, visibleEnNav } from './nav';
import { SelectorEmpresa } from './SelectorEmpresa';

export function MobileShell(props: {
  readonly sesion: SesionMe;
  readonly children: React.ReactNode;
  readonly onCerrar: () => void;
}): React.JSX.Element {
  const ruta = useRuta();
  const [mas, setMas] = useState(false);
  const [avisos, setAvisos] = useState(0);
  const mostrarMarca = leerTemaLocal()?.showPoweredBy !== false;
  const extras = destinosMas(props.sesion).filter((d) => visibleEnNav(props.sesion, d));

  useEffect(() => {
    void notificationsApi.noLeidas(props.sesion).then((r) => setAvisos(r.count)).catch(() => undefined);
  }, [props.sesion]);

  const nombre = leerTemaLocal()?.appName ?? textos.app.nombre;

  return (
    <div className="flex min-h-dvh flex-col bg-brand-surface-2" translate="no">
      <header className="flex items-center justify-between gap-3 border-b border-brand-border bg-brand-surface px-4 py-3">
        <p className="shrink-0 font-semibold text-brand-primary">{nombre}</p>
        <SelectorEmpresa sesion={props.sesion} />
      </header>
      <div className="flex-1 overflow-auto pb-24">{props.children}</div>
      <nav
        className="fixed bottom-0 left-0 right-0 z-cabecera flex items-end justify-around border-t border-brand-border bg-brand-surface pb-safe pt-2 shadow-[0_-4px_12px_hsl(var(--brand-text)/0.06)]"
        aria-label={textos.nav.inicio}
      >
        {NAV_MOVIL_FIJO.slice(0, 2).map((item) => (
          <BotonNav
            key={item.href}
            etiqueta={item.etiqueta}
            activo={rutaActiva(ruta, item.href)}
            onClick={() => irA(item.href)}
          />
        ))}
        <button
          type="button"
          className="-mt-6 flex h-14 min-w-[7rem] items-center justify-center rounded-full bg-brand-secondary px-4 font-semibold text-brand-secondary-fg"
          onClick={() => irA('/escanear')}
        >
          {textos.acciones.escanear}
        </button>
        <BotonNav
          etiqueta={textos.nav.operaciones}
          activo={rutaActiva(ruta, '/operaciones')}
          onClick={() => irA('/operaciones')}
        />
        <BotonNav
          etiqueta={avisos > 0 ? `${textos.nav.mas} (${avisos})` : textos.nav.mas}
          activo={mas || extras.some((e) => rutaActiva(ruta, e.href))}
          onClick={() => setMas(true)}
        />
      </nav>
      {mostrarMarca ? <p className="pb-safe text-center text-xs text-brand-text-muted">{textos.pie.poweredBy}</p> : null}
      {mas ? (
        <HojaInferior titulo={textos.nav.mas} onCerrar={() => setMas(false)}>
          <ul className="flex flex-col gap-2">
            {extras.map((item) => (
              <li key={item.href}>
                <Btn
                  variante="secundario"
                  className="w-full text-left"
                  onClick={() => {
                    setMas(false);
                    irA(item.href);
                  }}
                >
                  {item.etiqueta}
                  {item.href === '/avisos' && avisos > 0 ? ` (${avisos})` : ''}
                  {item.soloEscritorio ? ` · ${textos.dispositivo.escritorio}` : ''}
                </Btn>
              </li>
            ))}
            <li>
              <Btn variante="secundario" className="w-full text-left" onClick={props.onCerrar}>
                {textos.acciones.cerrarSesion}
              </Btn>
            </li>
          </ul>
        </HojaInferior>
      ) : null}
    </div>
  );
}

function BotonNav(props: {
  readonly etiqueta: string;
  readonly activo: boolean;
  readonly onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={`min-h-tactil px-3 text-sm ${props.activo ? 'font-semibold text-brand-primary' : 'text-brand-text'}`}
      aria-current={props.activo ? 'page' : undefined}
      onClick={props.onClick}
    >
      {props.etiqueta}
    </button>
  );
}
